import {
  AUDIT_CHANGED_KEY,
  AUDIT_CHANGE_PREFIXES,
  AUDIT_NONE,
  OBJECT_ID_REGEX,
  isAuditChangeField,
  type AuditAction,
  type AuditEntityType,
} from '@pams/shared';
import {
  ATTENDANCE_AUDIT_ACTIONS,
  ATTENDANCE_CONTEXT_KEY_LABEL,
  ATTENDANCE_CONTEXT_WORDS,
  ATTENDANCE_HIDDEN_CONTEXT_KEYS,
  AUDIT_CONTEXT_KEY_LABEL,
  COMMAND_SEAT_AUDIT_ACTIONS,
  COMMAND_SEAT_CONTEXT_KEY_LABEL,
  COMMAND_SEAT_HIDDEN_CONTEXT_KEYS,
  commandSeatTargetWords,
  AUDIT_FIELD_LABEL,
  DRAFT_PURGE_BASIS_WORD,
} from '@/lib/auditLabels';
import { istDateTime } from '@/lib/analyticsReport';
import { ACCESS_ROLE_LABEL, ORG_UNIT_SUBTYPE_LABEL, ORG_UNIT_ELEVATION_LABEL, ORG_UNIT_TYPE_LABEL } from '@/lib/labels';
import { AUDIT_LOG_COPY } from '@/pages/AuditLog/copy';

export type AuditChangeProps = {
  context: Readonly<Record<string, string>>;
  /**
   * ⚠️ REQUIRED, NEVER DEFAULTED TO `{}`. It is the server's names for the ids a context holds, and a
   * forgotten one would not show a hex string — it would silently HIDE every officer a swap row names.
   */
  contextNames: Readonly<Record<string, string | null>>;
  entityType: AuditEntityType;
  action: AuditAction;
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [ANALYTICS §8] "WHAT CHANGED", READ BACK OUT OF AN AUDIT CONTEXT.
 *
 *  The writers spell a diff one way (`constants/auditChange.constant.ts`) and this is its one
 *  reader: every field named in `changed` becomes a line — `Label: a → b` for a disclosed value,
 *  `Label: + a, b · − c` for a set change, and `Label changed` when the writer withheld the values.
 *  Whatever is left over (a create's identity keys, a version number, a flag) is a compact `<dl>`,
 *  so a row this reader does not understand still shows what it holds rather than nothing.
 *
 *  ⚠️ IT NEVER INVENTS A VALUE THE ROW DOES NOT CARRY, AND ON AN OFFICER IT REFUSES THE PERSONAL
 *  ONES EVEN IF A ROW DID. The API writes an officer's name, phone, email, service number and photo
 *  as `changed` only; if a future writer slipped and wrote `from:phone`, this renders "Phone changed"
 *  anyway. Two defences for one data class, `logger.ts`'s redaction-plus-serializer shape.
 *
 *  ⚠️ `SETTING_CHANGED` PREDATES THE CONVENTION and carries bare `key`/`from`/`to`, so it gets its own
 *  line rather than a migration of rows that are permanent by design.
 *
 *  ⚠️ AN ID IS NEVER DATA. A context key the server named (`contextNames`) shows that name, or "no
 *  longer exists"; an id-shaped value it did NOT name is hidden, because its readable twin (a version
 *  number beside a version id) is already on the row. [PRE-P6.2]'s "the stored row holds ids and the
 *  answer holds names", on the page that reads every row rather than one duty's.
 *
 *  ⚠️ `—` READS AS "none", AND A LONG LINE FOLDS BEHIND `<details>` — a policy description is two
 *  hundred characters and a column set can name thirty masters. `<details>` rather than a `Tooltip`:
 *  it is in the DOM whether open or not, and it is not an overlay the census has to account for.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

const PERSONAL_OFFICER_FIELDS: ReadonlySet<string> = new Set(['name', 'phone', 'email', 'serviceNumber', 'photoUrl']);

/** A line longer than this, or a set of more members, folds behind a disclosure. */
const LONG_LINE_CHARS = 120;
const LONG_SET_MEMBERS = 6;

type Line = { key: string; text: string; summary: string | null };

const shown = (value: string | undefined): string => (value === undefined || value === AUDIT_NONE ? 'none' : value);

/*
 * An enum member a writer stored reads in the words the rest of the app uses for it — `Nodal (read)`,
 * never `NODAL_READ`. A value the table does not know (a member retired later) prints as stored.
 */
const ENUM_WORDS: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  accessRole: ACCESS_ROLE_LABEL,
  type: ORG_UNIT_TYPE_LABEL,
  subType: ORG_UNIT_SUBTYPE_LABEL,
  elevation: ORG_UNIT_ELEVATION_LABEL,
  /* [C5c §8] A purge row's door — the creator is its actor, so the words say a rule did it. */
  basis: DRAFT_PURGE_BASIS_WORD,
};
const valueOf = (field: string, value: string | undefined): string =>
  (value === undefined ? undefined : ENUM_WORDS[field]?.[value]) ?? shown(value);

const members = (value: string | undefined): string[] =>
  value === undefined ? [] : value.split(',').map((member) => shown(member));

/** `clearedPlaceholderPhone` → `Cleared placeholder phone`. A key the field table does not name. */
const humanise = (key: string): string => {
  const words = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const labelOf = (key: string): string =>
  isAuditChangeField(key) ? AUDIT_FIELD_LABEL[key] : (AUDIT_CONTEXT_KEY_LABEL[key] ?? humanise(key));

/** Every comma-joined member shaped like an ObjectId — `addressees` is a list of them. */
const isIdValue = (value: string): boolean => value.split(',').every((member) => OBJECT_ID_REGEX.test(member));

/*
 * ⚠️ [C5c §8] A PURGE ROW'S TWO INSTANTS READ IN IST. The writer stores `toISOString()`, which is the
 * UTC instant — so a draft started at 00:30 IST read as the day before, on the one record that
 * outlives the draft ([IST-FIX]). `—` still reads "none", and a value that is not an instant prints
 * as stored rather than as "Invalid DateTime".
 */
const INSTANT_CONTEXT_KEYS: ReadonlySet<string> = new Set(['draftCreatedAt', 'draftSince']);
const instantOf = (value: string): string | null =>
  value !== AUDIT_NONE && !Number.isNaN(Date.parse(value)) ? `${istDateTime(value)} IST` : null;

const displayValue = (key: string, value: string): string =>
  value === 'true'
    ? 'Yes'
    : value === 'false'
      ? 'No'
      : ((INSTANT_CONTEXT_KEYS.has(key) ? instantOf(value) : null) ?? valueOf(key, value));

const describe = (
  context: Readonly<Record<string, string>>,
  contextNames: Readonly<Record<string, string | null>>,
  entityType: AuditEntityType,
  action: AuditAction,
): { lines: Line[]; leftovers: [string, string][]; attendance: boolean; commandSeat: boolean } => {
  const consumed = new Set<string>();
  const lines: Line[] = [];
  const withheld = (field: string): boolean => entityType === 'OFFICER' && PERSONAL_OFFICER_FIELDS.has(field);

  if (
    action === 'SETTING_CHANGED' &&
    context.key !== undefined &&
    (context.from !== undefined || context.to !== undefined)
  ) {
    for (const key of ['key', 'from', 'to']) consumed.add(key);
    const text = `${context.key}: ${shown(context.from)} → ${shown(context.to)}`;
    lines.push({ key: 'setting', text, summary: text.length > LONG_LINE_CHARS ? `${context.key} changed` : null });
  }

  const changed = (context[AUDIT_CHANGED_KEY] ?? '').split(',').filter((field) => field.length > 0);
  if (context[AUDIT_CHANGED_KEY] !== undefined) consumed.add(AUDIT_CHANGED_KEY);

  for (const field of changed) {
    const label = labelOf(field);
    /* ⚠️ The prefixes are the shared constant's — a second literal `'from:'` is the drift that
       renders a real change as a leftover. */
    const keyOf = (part: keyof typeof AUDIT_CHANGE_PREFIXES): string => {
      const key = `${AUDIT_CHANGE_PREFIXES[part]}${field}`;
      consumed.add(key);
      return key;
    };
    const [from, to, added, removed] = [
      context[keyOf('from')],
      context[keyOf('to')],
      context[keyOf('added')],
      context[keyOf('removed')],
    ];

    if (withheld(field)) {
      lines.push({ key: field, text: `${label} changed`, summary: null });
    } else if (from !== undefined || to !== undefined) {
      const text = `${label}: ${valueOf(field, from)} → ${valueOf(field, to)}`;
      lines.push({ key: field, text, summary: text.length > LONG_LINE_CHARS ? `${label} changed` : null });
    } else if (added !== undefined || removed !== undefined) {
      const plus = members(added);
      const minus = members(removed);
      const parts = [
        plus.length > 0 ? `+ ${plus.join(', ')}` : null,
        minus.length > 0 ? `− ${minus.join(', ')}` : null,
      ];
      const text = `${label}: ${parts.filter((part) => part !== null).join(' · ')}`;
      const long = plus.length + minus.length > LONG_SET_MEMBERS || text.length > LONG_LINE_CHARS;
      lines.push({
        key: field,
        text,
        summary: long ? `${label}: ${plus.length} added · ${minus.length} removed` : null,
      });
    } else {
      lines.push({ key: field, text: `${label} changed`, summary: null });
    }
  }

  /*
   * ⚠️ [C5c §5g] AN ATTENDANCE ROW WORDS ITS OWN CODES and hides the stop id — `auditLabels.ts`
   * says why the table is scoped to those actions rather than joining the per-field one above.
   */
  const attendance = ATTENDANCE_AUDIT_ACTIONS.has(action);
  /* ⚠️ [C6a review] A command seat row's minted handles — `auditLabels.ts` says why. */
  const commandSeat = COMMAND_SEAT_AUDIT_ACTIONS.has(action);

  const leftovers = Object.entries(context).flatMap(([key, value]): [string, string][] => {
    if (consumed.has(key)) return [];
    if (attendance && ATTENDANCE_HIDDEN_CONTEXT_KEYS.has(key)) return [];
    if (commandSeat && COMMAND_SEAT_HIDDEN_CONTEXT_KEYS.has(key)) return [];
    if (commandSeat && key === 'targets') return [[key, commandSeatTargetWords(value)]];
    /* ⚠️ A stray personal value on an officer row is dropped here too — FIRST, so no name can re-admit it. */
    const field = Object.values(AUDIT_CHANGE_PREFIXES).reduce(
      (rest, prefix) => (rest.startsWith(prefix) ? rest.slice(prefix.length) : rest),
      key,
    );
    if (withheld(field)) return [];
    if (Object.hasOwn(contextNames, key)) return [[key, contextNames[key] ?? AUDIT_LOG_COPY.gone]];
    /* ⚠️ An id nobody named is not shown at all — see the header. */
    if (isIdValue(value)) return [];
    const word = attendance ? ATTENDANCE_CONTEXT_WORDS[key]?.[value] : undefined;
    return [[key, word ?? displayValue(key, value)]];
  });
  return { lines, leftovers, attendance, commandSeat };
};

export const AuditChange = ({ context, contextNames, entityType, action }: AuditChangeProps) => {
  const { lines, leftovers, attendance, commandSeat } = describe(context, contextNames, entityType, action);
  const leftoverLabel = (key: string): string =>
    (attendance ? ATTENDANCE_CONTEXT_KEY_LABEL[key] : undefined) ??
    (commandSeat ? COMMAND_SEAT_CONTEXT_KEY_LABEL[key] : undefined) ??
    labelOf(key);

  if (lines.length === 0 && leftovers.length === 0) {
    return <span className="text-subtle">—</span>;
  }

  return (
    <div className="flex min-w-0 flex-col gap-1 text-sm" data-testid="audit-change">
      {lines.map((line) =>
        line.summary === null ? (
          <p key={line.key} className="break-words">
            {line.text}
          </p>
        ) : (
          <details key={line.key}>
            <summary className="cursor-pointer">{line.summary}</summary>
            <p className="mt-1 break-words text-muted-foreground">{line.text}</p>
          </details>
        ),
      )}
      {leftovers.length > 0 && (
        <dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
          {leftovers.map(([key, value]) => (
            <div key={key} className="contents">
              <dt>{leftoverLabel(key)}</dt>
              <dd className="min-w-0 break-words">{value}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
};
